import { useRef, useState } from "react";
import { BarChart3Icon } from "lucide-react";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { formatDateTime } from "@/lib/format";

type ClientStats = components["schemas"]["ClientStatsResponse"];

function toNumber(value: number | string | undefined): number | null {
  if (value === undefined || value === null) return null;
  const num = typeof value === "string" ? parseFloat(value) : value;
  return Number.isNaN(num) ? null : num;
}

interface ClientStatsHoverCardProps {
  clientId: string;
}

/** Loads the client's stats lazily on first hover. */
export function ClientStatsHoverCard({ clientId }: ClientStatsHoverCardProps) {
  const [stats, setStats] = useState<ClientStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestedRef = useRef(false);

  async function loadStats() {
    if (requestedRef.current) return;
    requestedRef.current = true;
    setLoading(true);
    setError(null);
    // Rendered per row in client lists, so target this client via a per-request header.
    const { data, error: apiError } = await api.GET("/v1/client/stats", {
      headers: { "x-dolfin-client-id": clientId },
    });
    if (apiError) {
      setError("Failed to load stats.");
      requestedRef.current = false; // allow retry on next hover
    } else {
      setStats(data ?? null);
    }
    setLoading(false);
  }

  const tiles: { label: string; value: number | null }[] = stats
    ? [
        { label: "Organisations", value: toNumber(stats.organisations) },
        { label: "Users", value: toNumber(stats.users) },
        { label: "Portal members", value: toNumber(stats.portalMembers) },
        { label: "API keys", value: toNumber(stats.apiKeys) },
        { label: "Webhooks", value: toNumber(stats.webhookSubscriptions) },
        { label: "Invoices", value: toNumber(stats.invoices) },
        { label: "Bills", value: toNumber(stats.bills) },
        { label: "Expenses", value: toNumber(stats.expenses) },
        { label: "Purchase orders", value: toNumber(stats.purchaseOrders) },
        { label: "Customers", value: toNumber(stats.customers) },
        { label: "Suppliers", value: toNumber(stats.suppliers) },
        { label: "Products", value: toNumber(stats.products) },
      ]
    : [];

  return (
    <HoverCard openDelay={150} closeDelay={100} onOpenChange={(open) => open && loadStats()}>
      <HoverCardTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-1 text-muted-foreground">
          <BarChart3Icon className="size-4" />
          Stats
        </Button>
      </HoverCardTrigger>
      <HoverCardContent align="end" className="w-72">
        {loading ? (
          <div className="flex items-center justify-center py-4">
            <Spinner className="size-5" />
          </div>
        ) : error ? (
          <p className="text-destructive text-sm">{error}</p>
        ) : stats ? (
          <div>
            <dl className="grid [&>div]:min-w-0 grid-cols-2 gap-x-4 gap-y-2">
              {tiles.map((tile) => (
                <div key={tile.label} className="flex items-center justify-between gap-2">
                  <dt className="text-muted-foreground text-xs">{tile.label}</dt>
                  <dd className="text-sm font-semibold tabular-nums">
                    {tile.value === null ? "—" : tile.value.toLocaleString()}
                  </dd>
                </div>
              ))}
            </dl>
            <p className="text-muted-foreground mt-3 text-[11px]">
              As of {formatDateTime(stats.asOf)}
            </p>
          </div>
        ) : (
          <p className="text-muted-foreground text-sm">No stats available.</p>
        )}
      </HoverCardContent>
    </HoverCard>
  );
}
